import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Download, Printer, Share2, FileText } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/useSubscription";
import { exportProfileToPDF } from "@/utils/pdfExport";
import { trackQRCodeDownload } from "@/utils/analytics";

interface Medication { name: string; notes?: string; }
interface Allergy { name: string; notes?: string; }
interface Condition { name: string; notes?: string; attackStatus?: string; }

interface EmergencyProfile {
  id: string;
  photo: string;
  profileImageUrl?: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  bloodType: string;
  emergencyContacts: { name: string; phone: string; relation: string }[];
  medications: Medication[];
  allergies: Allergy[];
  conditions: Condition[];
  additionalNotes: string;
}

const QRCodeView = () => {
  const { id } = useParams();
  const [profile, setProfile] = useState<EmergencyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [exportingPDF, setExportingPDF] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);
  const { isPremium, isPro } = useSubscription();

  useEffect(() => {
    const fetchProfile = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", id)
          .maybeSingle();

        if (error) {
          console.error("Error fetching profile:", error);
          setLoading(false);
          return;
        }

        if (data) {
          // Transform database format to component format
          const parseMeds = (data: any) => Array.isArray(data) ? data.map((m: any) => typeof m === 'string' ? {name: m} : m) : [];
          const parseAllergies = (data: any) => Array.isArray(data) ? data.map((a: any) => typeof a === 'string' ? {name: a} : a) : [];
          const parseConds = (data: any) => Array.isArray(data) ? data.map((c: any) => typeof c === 'string' ? {name: c} : c) : [];

          setProfile({
            id: data.id,
            photo: data.photo_url,
            profileImageUrl: data.profile_image_url,
            firstName: data.first_name,
            lastName: data.last_name,
            dateOfBirth: data.date_of_birth || "",
            bloodType: data.blood_type || "",
            emergencyContacts: data.emergency_contacts as { name: string; phone: string; relation: string }[],
            medications: parseMeds(data.medications),
            allergies: parseAllergies(data.allergies),
            conditions: parseConds(data.conditions),
            additionalNotes: data.additional_notes || "",
          });
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [id]);

  const handleDownload = () => {
    if (!qrRef.current) return;
    
    const canvas = document.createElement("canvas");
    const svg = qrRef.current.querySelector("svg");
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            const link = document.createElement("a");
            link.download = `mediqr-${profile?.firstName}-${profile?.lastName}.png`;
            link.href = URL.createObjectURL(blob);
            link.click();
            toast.success("QR-Code heruntergeladen!");
            
            // Track QR code download
            if (id) {
              trackQRCodeDownload(id, 'png');
            }
          }
        });
      }
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const handlePrint = () => {
    window.print();
    toast.success("Druckvorschau geöffnet");
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/profile/${id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `MediQR - ${profile?.firstName} ${profile?.lastName} Notfallprofil`,
          url: url,
        });
        toast.success("Link geteilt!");
      } catch (err) {
        console.error("Fehler beim Teilen:", err);
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link in Zwischenablage kopiert!");
    }
  };

  const handleExportPDF = async () => {
    if (!isPremium && !isPro) {
      toast.error("PDF-Export ist nur für Premium und Pro Mitglieder verfügbar");
      return;
    }

    if (!profile || !id) {
      toast.error("Profil nicht gefunden");
      return;
    }

    setExportingPDF(true);
    try {
      // Get QR code as data URL
      const svg = qrRef.current?.querySelector("svg");
      if (!svg) {
        throw new Error("QR-Code nicht gefunden");
      }

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas context not available");

      const svgData = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      const url = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));

      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          resolve();
        };
        img.onerror = reject;
        img.src = url;
      });

      const qrCodeDataUrl = canvas.toDataURL("image/png");

      // Fetch profile data from database
      const { data: profileData, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", id)
        .single();

      if (error || !profileData) {
        throw new Error("Fehler beim Laden der Profildaten");
      }

      // Export to PDF
      const parseMeds = (data: any) => Array.isArray(data) ? data.map((m: any) => typeof m === 'string' ? {name: m} : m) : [];
      const parseAllergies = (data: any) => Array.isArray(data) ? data.map((a: any) => typeof a === 'string' ? {name: a} : a) : [];
      const parseConds = (data: any) => Array.isArray(data) ? data.map((c: any) => typeof c === 'string' ? {name: c} : c) : [];

      await exportProfileToPDF(
        {
          ...profileData,
          emergency_contacts: profileData.emergency_contacts as Array<{ name: string; phone: string; relation: string }>,
          medications: parseMeds(profileData.medications),
          allergies: parseAllergies(profileData.allergies),
          conditions: parseConds(profileData.conditions),
        },
        qrCodeDataUrl
      );
      toast.success("PDF erfolgreich erstellt!");
      
      // Track PDF download
      if (id) {
        trackQRCodeDownload(id, 'pdf');
      }
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Fehler beim Erstellen des PDFs");
    } finally {
      setExportingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <Card className="p-8 glass text-center">
          <p className="text-lg text-muted-foreground">Lädt Profil...</p>
        </Card>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <Card className="p-8 glass text-center">
          <p className="text-lg text-muted-foreground">Profil nicht gefunden</p>
          <Link to="/">
            <Button className="mt-4">Zur Startseite</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const profileUrl = `${window.location.origin}/profile/${id}`;

  return (
    <div className="min-h-screen bg-gradient-subtle py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <div className="no-print mb-6">
          <Link to="/">
            <Button variant="ghost">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Zurück
            </Button>
          </Link>
        </div>

        <div className="mb-8 no-print">
          <h1 className="text-4xl font-bold mb-2">
            Ihr <span className="gradient-text">QR-Code</span>
          </h1>
          <p className="text-muted-foreground">
            Drucken oder laden Sie Ihren personalisierten Notfall-QR-Code herunter.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* QR Code Card */}
          <Card className="p-8 glass flex flex-col items-center justify-center space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold mb-2">
                {profile.firstName} {profile.lastName}
              </h2>
              <p className="text-sm text-muted-foreground">Notfallprofil QR-Code</p>
            </div>

            {profile.profileImageUrl && (
              <div className="flex justify-center">
                <img
                  src={profile.profileImageUrl}
                  alt="Profil-Kennzeichnung"
                  className="w-20 h-20 rounded-lg object-cover border-2 border-primary/30 shadow-md"
                />
              </div>
            )}

            <div ref={qrRef} className="relative bg-white p-6 rounded-2xl shadow-lg">
              <QRCodeSVG
                value={profileUrl}
                size={280}
                level="H"
                includeMargin={true}
                imageSettings={{
                  src: profile.photo,
                  height: 60,
                  width: 60,
                  excavate: true,
                }}
              />
            </div>

            <div className="w-full space-y-3 no-print">
              <Button onClick={handleDownload} className="w-full" variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Als Bild herunterladen
              </Button>
              <Button onClick={handlePrint} className="w-full" variant="outline">
                <Printer className="mr-2 h-4 w-4" />
                Drucken
              </Button>
              {(isPremium || isPro) && (
                <Button 
                  onClick={handleExportPDF} 
                  className="w-full" 
                  variant="outline"
                  disabled={exportingPDF}
                >
                  {exportingPDF ? (
                    <>
                      <FileText className="mr-2 h-4 w-4 animate-pulse" />
                      PDF wird erstellt...
                    </>
                  ) : (
                    <>
                      <FileText className="mr-2 h-4 w-4" />
                      Als PDF exportieren
                    </>
                  )}
                </Button>
              )}
              <Button onClick={handleShare} className="w-full bg-gradient-primary">
                <Share2 className="mr-2 h-4 w-4" />
                Link teilen
              </Button>
            </div>
          </Card>

          {/* Profile Preview */}
          <Card className="p-8 glass space-y-6">
            <div className="flex items-center gap-4">
              <img
                src={profile.photo}
                alt={`${profile.firstName} ${profile.lastName}`}
                className="w-20 h-20 rounded-full object-cover border-4 border-primary/20"
              />
              <div>
                <h3 className="text-2xl font-semibold">
                  {profile.firstName} {profile.lastName}
                </h3>
                {profile.bloodType && (
                  <p className="text-muted-foreground">Blutgruppe: {profile.bloodType}</p>
                )}
              </div>
            </div>

            <div className="space-y-4 text-sm">
              {profile.emergencyContacts.some((c) => c.name) && (
                <div>
                  <h4 className="font-semibold text-primary mb-2">Notfallkontakte</h4>
                  {profile.emergencyContacts.filter((c) => c.name).map((contact, i) => (
                    <div key={i} className="text-muted-foreground">
                      {contact.name} ({contact.relation}): {contact.phone}
                    </div>
                  ))}
                </div>
              )}

              {profile.medications.some((m) => m.name) && (
                <div>
                  <h4 className="font-semibold text-primary mb-2">Medikamente</h4>
                  <ul className="list-disc list-inside text-muted-foreground">
                    {profile.medications.filter((m) => m.name).map((med, i) => (
                      <li key={i}>{med.name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {profile.allergies.some((a) => a.name) && (
                <div>
                  <h4 className="font-semibold text-destructive mb-2">Allergien</h4>
                  <ul className="list-disc list-inside text-muted-foreground">
                    {profile.allergies.filter((a) => a.name).map((allergy, i) => (
                      <li key={i}>{allergy.name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {profile.conditions.some((c) => c.name) && (
                <div>
                  <h4 className="font-semibold text-primary mb-2">Vorerkrankungen</h4>
                  <ul className="list-disc list-inside text-muted-foreground">
                    {profile.conditions.filter((c) => c.name).map((condition, i) => (
                      <li key={i}>{condition.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Print Instructions */}
        <Card className="mt-8 p-6 glass no-print">
          <h3 className="text-xl font-semibold mb-4">💡 Tipps zur Verwendung</h3>
          <ul className="space-y-2 text-muted-foreground">
            <li>• Platzieren Sie den QR-Code gut sichtbar am Kühlschrank</li>
            <li>• Bewahren Sie eine Kopie in Ihrer Geldbörse auf</li>
            <li>• Für beste Scanergebnisse auf hellem Papier drucken</li>
            <li>• Aktualisieren Sie das Profil regelmäßig bei Änderungen</li>
          </ul>
        </Card>
      </div>

      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
          }
        }
      `}</style>
    </div>
  );
};

export default QRCodeView;
